import _ from 'lodash'
import T from 'prop-types'
import React from 'react'
import {
  RULE_HEADER_SCHEMA,
  RULE_OPTION_SCHEMA,
  REQUIRED_FIELD_NAMES,
} from '../constants/suricataSchema'
import ListInput from './ListInput'
import {
  IDE_PART_TYPES,
  stripSmartQuotes
} from '../editorInputUtils'

const RULE_PLACEHOLDER = `alert http $HOME_NET any -> $EXTERNAL_NET any (msg:"..."; classtype:...; sid:1;)`

const UNKNOWN_RULE_FLAG = {
  fieldName: "_unknownFlag",
  className: `rule_unknown_flag has_warning`,
  tooltip: "Warning: Unknown content modifier or keyword."
}
const UNKNOWN_RULE_OPTION = {
  fieldName: "_unknownRule",
  className: `rule_unknown_option has_warning`,
  tooltip: "Warning: Unknown rule option."
}

function countLeadingSpaces (str) {
  return str.search(/\S|$/)
}


/* Must cover all of these cases for Hosts
! 1.1.1.1                       (Every IP address but 1.1.1.1)
![1.1.1.1, 1.1.1.2]             (Every IP address but 1.1.1.1 and 1.1.1.2)
$HOME_NET                       (Your setting of HOME_NET in yaml)
[$EXTERNAL_NET, !$HOME_NET]     (EXTERNAL_NET and not HOME_NET)
[10.0.0.0/24, !10.0.0.5]        (10.0.0.0/24 except for 10.0.0.5)
[…..,[....]]
[…. ,![.....]]
*/
/* Must cover all of these cases for Ports
[80, 81, 82]    (port 80, 81 and 82)
[80: 82]        (Range from 80 till 82)
[1024: ]        (From 1024 till the highest port-number)
!80             (Every port but 80)
[80:100,!99]    (Range from 80 till 100 but 99 excluded)
[1:80,![2,4]]
[….[.....]]
*/

const RULE_HEADER_PARTS = Object.keys(RULE_HEADER_SCHEMA)

function addHeaderPartToOutput(outputParts, currentPart, currentIndex, baseOffset) {
  const partLength = currentPart.length
  const partOffset = baseOffset + (currentIndex - partLength)

  let outputPart = {
    offset: partOffset,
    length: partLength,
    text: currentPart
  }

  if (outputParts.length >= RULE_HEADER_PARTS.length) {
    outputPart.invalidMsg = "Invalid Rule Part"
    outputPart.partType = IDE_PART_TYPES.INVALID_RANGE
  }

  outputParts.push(outputPart)
}

function splitHeaderParts (headerStr = "", baseOffset = 0) {
  const headerStrLen = headerStr.length
  let currentIndex = 0
  let current = ""
  let prev = ""
  let currentPart = ""
  let outputParts = []
  let _currentStart = ""
  let _leadingBracketCt = 1 // always start with the initial opening bracket

  while (currentIndex !== headerStrLen) {
    current = headerStr[currentIndex]

    if (current === " " && _currentStart === "" && prev !== " " && prev !== "!") {
      addHeaderPartToOutput(outputParts, currentPart, currentIndex, baseOffset)
      currentPart = ""
    }
    else if (current === "[") {
      // Start of a part sequence that may contain inner spaces
      if (_currentStart === "[") {
        _leadingBracketCt += 1 // Nested brackets, track count
      }
      _currentStart = current
      currentPart += current
    }
    else if (current === "]") {
      if (_leadingBracketCt === 1) {
        // Finished with this bracketed segment, unset currentStart
        _currentStart = ""
      }
      else {
        _leadingBracketCt -= 1
      }
      currentPart += current
    }
    else {
      currentPart += current
    }

    prev = current
    currentIndex += 1
  }

  return outputParts
}


function splitRuleOptions (optionsStr = "", baseOffset = 0) {
  const optionsStrLen = optionsStr.length
  let currentIndex = 0
  let current = ""
  let prev = ""
  let currentKey = ""
  let currentValue = ""
  let key = ""
  let outputParts = []

  while (currentIndex !== optionsStrLen) {
    current = optionsStr[currentIndex]
    if (key === "" && current === ":") {
      key = currentKey

    }
    else if (current === ";" && prev !== "\\") {
      const keyTrimmed = currentKey.trim()

      if (currentValue !== "") {
        const fullClause = currentKey + ":" + currentValue //+ current
        const fullClauseLength = fullClause.length
        const partOffset = baseOffset + (currentIndex - fullClauseLength)
        const valueTrimmed = currentValue.trim()
        outputParts.push({
          partType: IDE_PART_TYPES.KEY_VALUE,
          text: fullClause,
          offset: partOffset,
          length: fullClauseLength,
          children: {
            key: {
              offset: partOffset + countLeadingSpaces(currentKey),
              length: keyTrimmed.length,
              text: keyTrimmed,
            },
            value: {
              offset: partOffset + currentKey.length + 1 + countLeadingSpaces(currentValue),
              length: valueTrimmed.length,
              text: valueTrimmed,
            }
          }
        })
      }
      else {
        outputParts.push({
          partType: IDE_PART_TYPES.VALUE,
          offset: baseOffset + (currentIndex - keyTrimmed.length),
          length: keyTrimmed.length,
          text: keyTrimmed,
        })
      }
      key = ""
      currentKey = ""
      currentValue = ""
    }
    else {
      if (key === "") {
        currentKey += current
      }
      else {
        currentValue += current
      }
    }
    prev = current
    currentIndex += 1
  }

  // If currentValue & currentKey remain,
  // then there is a trailing semicolon missing
  if (currentValue !== "" && currentKey !== "") {
    // There are unmatched/invalid characters remaining
    const trimmedValue = currentValue.trim()
    outputParts.push({
      invalidMsg: "Missing semicolon (;) after Rule Option",
      partType: IDE_PART_TYPES.INVALID_RANGE,
      offset: baseOffset + (currentIndex - currentValue.length) + countLeadingSpaces(currentValue),
      length: trimmedValue.length,
      text: trimmedValue,
    })
  }
  else if (currentKey.trim() !== "") {
    // There are unmatched/invalid characters remaining
    const trimmedKey = currentKey.trim()
    outputParts.push({
      invalidMsg: "Unrecognized Rule Content",
      partType: IDE_PART_TYPES.INVALID_RANGE,
      offset: baseOffset + (currentIndex - currentKey.length) + countLeadingSpaces(currentKey),
      length: trimmedKey.length,
      text: trimmedKey,
    })
  }

  return outputParts
}

// const bracketsRegex = /\[([^\]]+)\]/g

function parseSuricataString (inputString = "", {dynamicValidators}) {
  if (inputString.length === 0) {
    return []
  }
  let isDisabled = false
  let ruleParts = []
  let baseOffset = 0

  // Handle disabled rules
  if (inputString.trim().charAt(0) === '#') {
    const disabledIndex = inputString.indexOf('#')
    isDisabled = true

    ruleParts.push({
      offset: disabledIndex,
      length: 1,
      text: '#',
      partType: IDE_PART_TYPES.VALUE,
      tooltip: "Rule Disabled",
      className: "rule_part rule_disabled_flag",
    })
    baseOffset = disabledIndex + 1
  }

  inputString = inputString.substring(baseOffset) // parse the rest of the string as usual

  // 1: split initial (known-order) action/header from (settings)
  const settingsStart = inputString.indexOf('(') //inputString.match(/\((?=(?:[^"]*"[^"]*")*[^"]*$)/)
  const header = settingsStart !== -1 ? inputString.slice(0, settingsStart) : inputString
  const splitHeader = splitHeaderParts(header, baseOffset)
  const headerAsIDEState = splitHeader.map((headerPart, i) => {
    headerPart.partType = IDE_PART_TYPES.VALUE

    let schemaDef = RULE_HEADER_SCHEMA[i] // Lookup by ordered index position
    if (schemaDef) {
      // headerPart.invalidMsg = schemaDef.validate && schemaDef.validate(headerPart.text)
      headerPart.className = schemaDef.className
      headerPart.tooltip = schemaDef.tooltip
      headerPart.label = schemaDef.label
      headerPart.fieldName = schemaDef.fieldName
      if (schemaDef.validate) {
        headerPart.validate = schemaDef.validate.bind(schemaDef)
      }
    }

    return headerPart
  })

  // 2: Split freeform key-value/modifier settings within parentheses
  let settingsAsIDEState = []
  if (settingsStart !== -1) {
    const settings = inputString.slice(settingsStart).trim()
    const closingParenIdx = settings.lastIndexOf(")")
    const settingsText = settings.substring(1, closingParenIdx !== -1 ? closingParenIdx : settings.length) // Lop off parentheses
    const settingsStartIdx = settingsStart + 1 + baseOffset
    // settingsAsIDEState = splitSettingsParts(settingsText, settingsStartIdx)
    settingsAsIDEState = splitRuleOptions(settingsText, settingsStartIdx)

    if (closingParenIdx === -1) {
      settingsAsIDEState.push({
        invalidMsg: `Missing closing ")"`,
        partType: IDE_PART_TYPES.INVALID_RANGE,
        offset: settingsStartIdx,
        length: settingsText.length,
        text: settingsText,
      })
    }
    else if (closingParenIdx < (settings.length - 1)) {
      settingsAsIDEState.push({
        invalidMsg: `No characters allowed after ")"`,
        partType: IDE_PART_TYPES.INVALID_RANGE,
        offset: settingsStartIdx + closingParenIdx,
        length: settings.length - 1 - closingParenIdx,
        text: settings.substring(closingParenIdx + 1),
      })
    }


    settingsAsIDEState = settingsAsIDEState.map(settingPart => {
      let schemaDef = null
      switch (settingPart.partType) {
        case IDE_PART_TYPES.VALUE:
          schemaDef = RULE_OPTION_SCHEMA[settingPart.text] || UNKNOWN_RULE_FLAG
          settingPart.fieldName = schemaDef.fieldName
          settingPart.validate = schemaDef.validate && schemaDef.validate.bind(schemaDef)
          settingPart.preventMultiple = schemaDef.preventMultiple
          settingPart.className = schemaDef.className
          settingPart.tooltip = schemaDef.tooltip
          settingPart.label = schemaDef.label
          break
        case IDE_PART_TYPES.KEY_VALUE:
          schemaDef = RULE_OPTION_SCHEMA[settingPart.children.key.text] || UNKNOWN_RULE_OPTION
          settingPart.fieldName = schemaDef.fieldName
          settingPart.validate = schemaDef.validate && schemaDef.validate.bind(schemaDef)
          settingPart.preventMultiple = schemaDef.preventMultiple
          settingPart.children.key.className = schemaDef.className
          settingPart.children.key.tooltip = schemaDef.tooltip
          settingPart.children.value.className = schemaDef.className
          settingPart.children.value.tooltip = schemaDef.tooltip
          settingPart.label = schemaDef.label
          break
        default:
          schemaDef = null
          // No default validation
      }

      return settingPart
    })

    // TOOD does the group wrap make it too noisy?
    settingsAsIDEState.push({
      partType: IDE_PART_TYPES.GROUP,
      text: settings,
      offset: settingsStart,
      length: settings.length,
    })
  }

  let outputIDEState = ruleParts.concat(headerAsIDEState.concat(settingsAsIDEState))

  // Run all parts through validation
  // TODO split this out to a separate function and call it on a debounce
  let allValidationMessages = []
  let currentFieldNames = []

  outputIDEState = _.map(outputIDEState, part => {
    let invalidMsg = null

    // if (part.invalidMsg) {
    //   allValidationMessages.push(part.invalidMsg)
    //   if (part.partType === IDE_PART_TYPES.KEY_VALUE) {
    //     part.children.value.invalidMsg = part.invalidMsg
    //   }
    //   return part
    // }

    if (part.fieldName) {
      if (!!part.preventMultiple && currentFieldNames.indexOf(part.fieldName) !== -1) {
        invalidMsg = `${ part.fieldName } - Multiple occurrences are not allowed.`
        allValidationMessages.push(invalidMsg)
        part.children.key.invalidMsg = invalidMsg
        return part
      }
      currentFieldNames.push(part.fieldName)
    }
    const validatorFn = dynamicValidators && dynamicValidators[part.fieldName] ? dynamicValidators[part.fieldName] : part.validate

    switch (part.partType) {
      case IDE_PART_TYPES.VALUE:
        invalidMsg = validatorFn && validatorFn(part.text, part.fieldName, outputIDEState)

        if (invalidMsg) {
          invalidMsg = `${ part.label || part.fieldName } - ${ invalidMsg }`
          allValidationMessages.push(invalidMsg)
          part.invalidMsg = invalidMsg
        }
        break
      case IDE_PART_TYPES.KEY_VALUE:
        invalidMsg = validatorFn && validatorFn(part.children.value.text, part.fieldName, outputIDEState)
        if (invalidMsg) {
          allValidationMessages.push(`${ part.label || part.fieldName } - ${ invalidMsg }`)
          part.children.value.invalidMsg = invalidMsg
        }
        break
      case IDE_PART_TYPES.INVALID_RANGE:
        invalidMsg = part.invalidMsg
        if (invalidMsg) {
          allValidationMessages.push(`${ part.offset } - ${ invalidMsg }`)
        }
        break
      default:
        invalidMsg = null
        // No default validation
    }
    return part
  })

  // Validate all required fields are present
  const missingRequiredFieldNames = _.difference(REQUIRED_FIELD_NAMES, currentFieldNames)
  if (missingRequiredFieldNames.length > 0) {
    allValidationMessages.unshift(`Missing Fields - ${ _.map(missingRequiredFieldNames).join(', ') }`)
  }

  // Validate that at least one non-meta option is present,
  // to prevent rules that would never match anything
  // if (_.intersection(ALL_CONTENT_FIELD_NAMES, currentFieldNames).length === 0) {
  //   allValidationMessages.unshift(`No Matchers - At least one content-matching rule option is required`)
  // }

  // console.log('++++++SuricateRuleInput IDE State++++++', outputIDEState, allValidationMessages)

  return {
    IDEState: outputIDEState,
    IDEStateData: {
      disabled: isDisabled,
      invalid: allValidationMessages.length > 0,
      invalidSummary: allValidationMessages
    }
  }
}


// const GID_REGEX = /(\sgid:)(\d+);/g
// const SID_REGEX = /(\ssid:)(\d+);/g
// const NUMBERS_REGEX = /(\d+)/g

// TODO add custom blockRenderer to...
// - add tooltip to rule body to open a modal "expanded" view with all settings
//   on separate lines

// function suricataBlockStyleFn (contentBlock) {
//   const {
//     disabled,
//     invalid
//   } = contentBlock.getData().toObject()
//   return cx(`suricata_rule`, {
//     'is_disabled': disabled,
//     'is_invalid': invalid
//   })
// }


// Suricata File Upload parser/handling


const SIMPLE_RULE_MATCH = /^(.[^\s]+)\s+(.[^\s]+)\s+(.[^\s]+)\s+(.[^\s]+)\s+(->|<-|<>)\s+(.[^\s]+)\s+(.[^\s]+)\s+\((.*)\)/

function _isValidRuleSimple (rule) {
  // Simple regex validator returns false for "#"-disabled rules,
  // strip it before validating
  if (rule[0] === '#' || rule[1] === '#') {
    rule = rule.replace('#', '').trim()
  }
  return SIMPLE_RULE_MATCH.test(rule)
}

function simpleSuricataFileTextParser (fileText) {
  // const textLines = inputText.split(/\n/)
  // const numLines = textLines.length
  //
  // let validLines = []
  // let invalidLines = []
  //
  //
  // let discardedLines = 0
  // let disabledRules = []
  // let enabledRules = []
  //
  // for (var i = 0; i < numLines; i++) {
  //   let line = textLines[i].trim()
  //   if (line.length > 0) {
  //     let _targetArr = enabledRules
  //     if (line[0] === '#' || line[1] === '#') {
  //       line = line.replace('#', '').trim()
  //       _targetArr = disabledRules
  //     }
  //     if (_isValidRuleSimple(line)) {
  //       _targetArr.push(stripSmartQuotes(line))
  //     }
  //     else {
  //       discardedLines++
  //     }
  //   }
  //   else {
  //     discardedLines++
  //   }
  // }
  // return {
  //   disabledRules,
  //   enabledRules,
  //   discardedLines,
  // }
  //



  const fileLines = fileText.split(/\n/)

  let validLines = []
  let invalidLines = []

  for (let i = 0, numLines = fileLines.length; i < numLines; i++) {
    const line = stripSmartQuotes(fileLines[i].trim())
    if (line.length > 0) {
      if (_isValidRuleSimple(line)) {
        // validLines.push(line)
        validLines.push({
          lineNumber: i,
          text: line
        })
      }
      else {
        // invalidLines.push(line)
        invalidLines.push({
          lineNumber: i,
          text: line
        })
      }
    }
  }

  return {
    validLines,
    invalidLines
  }

}



export default class SuricataRuleInput extends React.Component {
  static displayName = 'SuricataRuleInput';

  static propTypes = {
    allowFileUpload: T.bool,
    className: T.string,
    customValidators: T.object,
    restrictSingle: T.bool,
  }

  static defaultProps = {
    allowFileUpload: false,
    restrictSingle: true
  }

  setValue = (newValue) => {
    if (this._editor && newValue) {
      this._editor.setValue(newValue)
    }
  }

  resetToInitialValue = () => {
    if (this._editor) {
      this._editor.resetToInitialValue()
    }
  }

  _handleEditorRef = (inst) => {
    this._editor = inst
  }

  render() {
    const {props} = this
    const {
      allowFileUpload,
      className,
      customValidators,
    } = props

    return (
      <ListInput
        allowFileUpload={ allowFileUpload }
        allowedFileExtensions={ [ ".txt", ".rules" ] }
        className={ `suricata_rule_input_wrap ${ className || "" }` }
        editorClassName={ `suricata_rule_input` }
        fileParser={ simpleSuricataFileTextParser }
        lineLabel={ "Rule" }
        parser={ parseSuricataString }
        parserOptions={ {
          dynamicValidators: customValidators
        } }
        placeholder={ RULE_PLACEHOLDER }
        ref={ this._handleEditorRef }
        { ...this.props }
      />
    )
  }
}
